import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Cloud, CheckCircle, AlertCircle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Google OAuth Client ID - loaded from environment variable
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const REDIRECT_URI = `${window.location.origin}/drive-callback`;

function DriveSetup({ onComplete, onSkip }) {
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [showManualSetup, setShowManualSetup] = useState(false);

  useEffect(() => {
    // Check if we're returning from OAuth redirect
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const errorParam = urlParams.get('error');

    if (errorParam) {
      setError(`OAuth error: ${errorParam}`);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (code && state === 'drive_setup') {
      handleOAuthCallback(code);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleOAuthCallback = async (code) => {
    setIsConnecting(true);
    try {
      // Exchange code for token on backend
      const response = await axios.post(
        `${API}/auth/google-drive-callback`,
        { code, redirect_uri: REDIRECT_URI },
        { withCredentials: true }
      );

      toast.success('Google Drive connected successfully!');
      onComplete();
    } catch (err) {
      console.error('OAuth callback error:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to connect Google Drive';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsConnecting(false);
    }
  };

  const connectDrive = () => {
    setIsConnecting(true);
    setError(null);

    try {
      // Build OAuth URL for redirect flow
      const scope = 'https://www.googleapis.com/auth/drive.file';
      const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
      
      authUrl.searchParams.append('client_id', GOOGLE_CLIENT_ID);
      authUrl.searchParams.append('redirect_uri', REDIRECT_URI);
      authUrl.searchParams.append('response_type', 'code');
      authUrl.searchParams.append('scope', scope);
      authUrl.searchParams.append('access_type', 'offline');
      authUrl.searchParams.append('state', 'drive_setup');
      authUrl.searchParams.append('prompt', 'consent');

      // Redirect to Google OAuth
      window.location.href = authUrl.toString();
    } catch (err) {
      console.error('Connection error:', err);
      setError(err.message);
      setIsConnecting(false);
    }
  };

  const handleManualSetup = () => {
    setShowManualSetup(true);
  };

  if (showManualSetup) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-xl max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto"
        >
          <h2 className="text-2xl font-heading font-bold text-slate-900 mb-4">
            Google Drive Setup Guide
          </h2>
          
          <div className="space-y-4 text-sm text-slate-600">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="font-medium text-blue-900 mb-2">Option 1: Skip Drive Integration</p>
              <p className="text-blue-800">
                You can still download Excel files locally. Click "Skip for now" below to continue without Drive.
              </p>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <p className="font-medium text-slate-900 mb-2">Option 2: Configure Google OAuth (Admin)</p>
              <ol className="list-decimal list-inside space-y-2 text-slate-700">
                <li>Go to <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline inline-flex items-center">Google Cloud Console <ExternalLink className="w-3 h-3 ml-1" /></a></li>
                <li>Create a new project or select existing</li>
                <li>Enable "Google Drive API"</li>
                <li>Create OAuth 2.0 credentials (Web application)</li>
                <li>Add authorized redirect URI: <code className="bg-slate-100 px-1 rounded text-xs">{REDIRECT_URI}</code></li>
                <li>Add authorized JavaScript origins: <code className="bg-slate-100 px-1 rounded text-xs">{window.location.origin}</code></li>
                <li>Copy the Client ID and update the app configuration</li>
              </ol>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="font-medium text-amber-900 mb-2">Troubleshooting</p>
              <ul className="list-disc list-inside space-y-1 text-amber-800">
                <li>Ensure the Google Cloud project has Drive API enabled</li>
                <li>Check that redirect URI exactly matches the one configured</li>
                <li>Allow popups for this site in browser settings</li>
                <li>Try using a different browser if issues persist</li>
              </ul>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={() => setShowManualSetup(false)}
              className="px-6 py-2.5 bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 transition-all font-medium rounded-lg"
            >
              Back
            </button>
            <button
              onClick={onSkip}
              className="px-6 py-2.5 bg-indigo-700 text-white hover:bg-indigo-800 transition-all font-medium rounded-lg"
            >
              Skip for Now
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8"
      >
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center mx-auto mb-4">
            <Cloud className="w-8 h-8 text-indigo-700" />
          </div>
          <h2 className="text-2xl font-heading font-bold text-slate-900 mb-2">
            Connect Google Drive
          </h2>
          <p className="text-slate-600 text-sm">
            Automatically backup your invoice Excel files to Google Drive
          </p>
        </div>

        <div className="space-y-4 mb-6">
          <div className="flex items-start space-x-3 p-3 bg-slate-50 rounded-lg">
            <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-slate-900">Automatic Backup</p>
              <p className="text-xs text-slate-600">All invoices automatically saved</p>
            </div>
          </div>
          <div className="flex items-start space-x-3 p-3 bg-slate-50 rounded-lg">
            <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-slate-900">Secure Storage</p>
              <p className="text-xs text-slate-600">Industry-standard encryption</p>
            </div>
          </div>
          <div className="flex items-start space-x-3 p-3 bg-slate-50 rounded-lg">
            <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-slate-900">Easy Access</p>
              <p className="text-xs text-slate-600">Access from any device</p>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-rose-50 border border-rose-200 rounded-lg">
            <div className="flex items-start space-x-2">
              <AlertCircle className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-rose-800">{error}</p>
                <button
                  onClick={handleManualSetup}
                  className="text-xs text-rose-600 hover:text-rose-700 underline mt-1"
                >
                  View setup instructions
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <button
            onClick={connectDrive}
            disabled={isConnecting}
            className="w-full bg-indigo-700 text-white hover:bg-indigo-800 shadow-sm hover:shadow-md transition-all duration-200 font-medium px-6 py-3 rounded-lg flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="connect-drive-button"
          >
            {isConnecting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span>Connecting...</span>
              </>
            ) : (
              <>
                <Cloud className="w-5 h-5" />
                <span>Connect Google Drive</span>
              </>
            )}
          </button>

          <button
            onClick={onSkip}
            disabled={isConnecting}
            className="w-full text-slate-600 hover:text-slate-900 transition-colors font-medium px-6 py-2"
            data-testid="skip-drive-button"
          >
            Skip for now
          </button>
        </div>

        <p className="text-xs text-center text-slate-400 mt-4">
          You can connect Drive later from Settings
        </p>
      </motion.div>
    </div>
  );
}

export default DriveSetup;
